import type { ComposeCampaignRequest, VoiceCampaignFormValues } from './types';
import { IS_CONTINUOUS, templateFeatures } from './types';

/**
 * The form now stores the actual backend `templateId` ('7' / '8' / '9' for
 * the three TTS flavors), so this is just an identity passthrough — kept as
 * a named export so callers don't have to reach into the form values.
 */
export function resolveTemplateId(values: VoiceCampaignFormValues): string {
  return values.templateId;
}

/** Format a Date or ISO string to the legacy `YYYY-MM-DD HH:mm:ss` shape. */
export function formatScheduleTime(value: string | Date | null): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

/**
 * Build the JSON body the legacy `/api/obd/campaign/compose` endpoint
 * expects. `baseId` is resolved separately (uploaded contact/file →
 * baseId; existing → the picked id).
 */
export function buildComposeRequest(args: {
  userId: number;
  values: VoiceCampaignFormValues;
  baseId: number;
  /**
   * Lookup from locationId (string) → locationName. Used to assemble the
   * legacy `location` wire shape `{locationList: [{locationId, locationName}]}`.
   * The backend expects each entry to be a JSON OBJECT, not a bare string —
   * sending raw ids triggers "JSONArray[0] is not a JSONObject" on compose.
   */
  locationNameById?: Record<string, string>;
  /**
   * Lookup from cliId (string) → cli phone number. Same shape requirement
   * as `location` — legacy ships `cliList: [{cliId, cli}, …]`.
   */
  cliNameById?: Record<string, string>;
}): ComposeCampaignRequest {
  const { userId, values, baseId, locationNameById = {}, cliNameById = {} } = args;
  const templateId = resolveTemplateId(values);
  const f = templateFeatures(values.templateId, values.templateIdByTTS);

  // For optional voice files, only ship the PId if the toggle is on AND the
  // file is even applicable to this template.
  const optInclude = (name: 'welcome' | 'noinput' | 'wronginput' | 'thanks'): boolean => {
    if (f.requiredVoiceFiles.includes(name)) return true;
    if (!f.optionalVoiceFiles.includes(name)) return false;
    switch (name) {
      case 'welcome':
        return values.useWelcomeFile;
      case 'noinput':
        return values.useNoinputFile;
      case 'wronginput':
        return values.useWronginputFile;
      case 'thanks':
        return values.useThanksFile;
    }
  };

  return {
    userId,
    campaignName: values.campaignName.trim(),
    templateId,
    ttsId: values.ttsId,
    /*
     * The backend rejects an empty `scheduleTime` with "Invalid Schedule
     * Date and Time !!". Legacy always populates it — when the user hasn't
     * picked a specific date, it sends the current time (treated as "fire
     * now"). Mirror that behavior here.
     */
    scheduleTime:
      values.sendLater && values.scheduleDate
        ? formatScheduleTime(values.scheduleDate)
        : formatScheduleTime(new Date()),
    sendLater: values.sendLater,
    baseId,
    dtmf: values.dtmf.join(','),
    welcomePId: optInclude('welcome') ? values.welcomeId : '',
    menuPId: f.requiredVoiceFiles.includes('menu') ? values.menuId : '',
    noInputPId: optInclude('noinput') ? values.noinputId : '',
    wrongInputPId: optInclude('wronginput') ? values.wronginputId : '',
    thanksPId: optInclude('thanks') ? values.thanksId : '',
    smsSuccessApi: values.sendSmsSuccess ? values.smsApiSuccess : '',
    smsFailApi: values.sendSmsFail ? values.smsApiFail : '',
    smsDtmfApi: values.sendSmsDtmf ? values.smsApiDtmf : '',
    callDurationSMS: values.sendSmsSuccess ? values.callDurationSMS : 0,
    retries: Number(values.retries) || 0,
    retryInterval: (Number(values.retryInterval) || 0) * 60,
    ringDuration: Number(values.ringDuration) || undefined,
    /**
     * Legacy wire shape is `{ patchList: [{groupId, groupName, agentDtmf}, …] }`
     * stringified — preserve that exactly. Empty for non-patch templates.
     */
    agentRows: f.hasPatchList ? JSON.stringify({ patchList: values.patchList ?? [] }) : '',
    menuWaitTime: values.menuWaitTime,
    rePrompt: values.rePrompt,
    ivrId: values.ivrId,
    /**
     * Legacy wire shape — each entry MUST be `{locationId, locationName}`.
     * The backend `new JSONObject(location).getJSONArray("locationList")
     * .getJSONObject(0)` call rejects bare-string elements with
     * "JSONArray[0] is not a JSONObject".
     */
    location: JSON.stringify({
      locationList: values.locationIds.map((id) => ({
        locationId: id,
        locationName: locationNameById[id] ?? '',
      })),
    }),
    /**
     * Same object-wrapping requirement as `location` — each cli entry must
     * be `{cliId, cli}`. Empty string for "use shared pool".
     */
    clis:
      values.selectedCLI.length === 0
        ? ''
        : JSON.stringify({
            cliList: values.selectedCLI.map((id) => ({
              cliId: id,
              cli: cliNameById[id] ?? '',
            })),
          }),
    webhook: values.sendWebhook,
    webhookId: values.webhookId,
    noAgentId: f.requiredVoiceFiles.includes('noAgent') ? values.noAgentId : '',
    callPatchSuccessMessage: values.sendSmsPatchSuccess ? values.callPatchSuccessMessage : '',
    callPatchFailMessage: values.sendSmsPatchFail ? values.callPatchFailMessage : '',
    ttsFlowId: values.selectedTTSFlowId,
    repeatMenu: values.repeatMenu,
    startDate: formatScheduleTime(values.startDate),
    endDate: formatScheduleTime(values.endDate),
    continuousSchedule: values.continuousSchedule || '',
    continueWithBase: values.continueWithBase,
    templateIdByTTS: values.templateIdByTTS,
  };
}

/**
 * Template 10 → POST /api/obd/campaign/compose/outdialV2 (multipart).
 * All non-file fields ride as flat multipart strings (backend accepts both
 * this and a JSON `campaign` part — we send flat).
 *
 * The backend resolves the base itself from an `action` enum:
 *   action 0 = upload new base file   (needs baseFile + baseName)
 *   action 1 = reuse existing base    (needs baseId)
 *   action 2 = pasted contact list    (needs contactList)
 *   action 3 = download from baseUrl  (not used by this form)
 */
export function buildOutdialV2Body(args: {
  req: ComposeCampaignRequest;
  values: VoiceCampaignFormValues;
  baseFile: File | null;
  campaignId?: number;
}): FormData {
  const { req, values, baseFile, campaignId } = args;
  const fd = new FormData();
  // Full compose body as flat multipart fields.
  for (const [k, v] of Object.entries(req)) {
    if (v === undefined) continue;
    fd.append(k, String(v));
  }

  // ── action: baseFileMode → backend base-resolution enum ──
  const action =
    values.baseFileMode === 'newBaseFile' ? 0 : values.baseFileMode === 'oldBaseFile' ? 1 : 2; // 'contactList'
  fd.set('action', String(action));

  // ── inner template — backend requires 7/8/9 explicitly on the body ──
  fd.set('templateIdByTTS', String(values.templateIdByTTS));

  // fd.set('scheduleTime', String(""));

  // ── per-action fields ──
  switch (values.baseFileMode) {
    case 'newBaseFile':
      if (baseFile) fd.append('baseFile', baseFile, baseFile.name);
      fd.set('baseName', values.baseName.trim());
      break;
    case 'oldBaseFile':
      // baseId is already in req — resolveBaseId() put the picked id there.
      break;
    case 'contactList':
      fd.set('contactList', values.contactList);
      break;
  }

  // ── continuous window — backend validates presence + format ──
  fd.set('startDate', req.startDate!);
  fd.set('endDate', req.endDate!);

  if (campaignId != null) fd.set('campaignId', String(campaignId));

  return fd;
}
